import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

import type { NewNote, NoteCategory, RecordingNote } from "./types";

// ---------------------------------------------------------------------------
// Portable coaching / QA notes panel.
//
// This component has ZERO app-specific imports beyond the shared `types`. All
// data operations and viewer identity arrive as props, so it drops into the
// PCA player UI by wiring `onLoad`/`onAdd`/`onDelete` to PCA's API client and
// `viewerSub`/`canModerate` to PCA's user context. In this app, App.tsx wires
// them to api.ts + auth.ts.
//
//   onLoad   () => Promise<RecordingNote[]>          fetch notes for the recording
//   onAdd    (note: NewNote) => Promise<RecordingNote>   create a note
//   onDelete (noteId: string) => Promise<void>       delete a note
//   viewerSub      Cognito `sub` of the current user (for "is this my note?")
//   canModerate    true if the viewer may delete anyone's note (admin)
//   currentTimeMs  current playback position, if a player is mounted alongside
//   onSeek         (offsetMs) => void — host jumps the player to that position
// ---------------------------------------------------------------------------

const CATEGORIES: NoteCategory[] = ["coaching", "compliance", "praise", "follow-up"];
const MAX_TEXT = 2000;

type Props = {
  onLoad: () => Promise<RecordingNote[]>;
  onAdd: (note: NewNote) => Promise<RecordingNote>;
  onDelete: (noteId: string) => Promise<void>;
  viewerSub: string | null;
  canModerate: boolean;
  currentTimeMs?: number | null;
  onSeek?: (offsetMs: number) => void;
};

export default function RecordingNotes({
  onLoad,
  onAdd,
  onDelete,
  viewerSub,
  canModerate,
  currentTimeMs,
  onSeek,
}: Props) {
  const [notes, setNotes] = useState<RecordingNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compose form state.
  const [text, setText] = useState("");
  const [category, setCategory] = useState<NoteCategory | "">("");
  const [anchor, setAnchor] = useState(false); // attach the note to currentTimeMs
  const [submitting, setSubmitting] = useState(false);
  const [busyDelete, setBusyDelete] = useState<string | null>(null);

  const canAnchor = typeof currentTimeMs === "number" && currentTimeMs >= 0;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setNotes(await onLoad());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [onLoad]);

  useEffect(() => { refresh(); }, [refresh]);

  async function submit() {
    const t = text.trim();
    if (!t) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: NewNote = { text: t };
      if (category) body.category = category;
      if (anchor && canAnchor) body.offsetMs = Math.floor(currentTimeMs!);
      const created = await onAdd(body);
      setNotes((prev) => sortByCreatedAt([...prev, created]));
      setText("");
      setCategory("");
      setAnchor(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(noteId: string) {
    if (!confirm("Delete this note?")) return;
    setBusyDelete(noteId);
    setError(null);
    try {
      await onDelete(noteId);
      setNotes((prev) => prev.filter((n) => n.noteId !== noteId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyDelete(null);
    }
  }

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Notes
        </Typography>
        {loading && <CircularProgress size={14} />}
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {notes.length} {notes.length === 1 ? "note" : "notes"}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Existing notes, oldest first (matches the recording's timeline). */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        {notes.length === 0 && !loading && (
          <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
            No notes yet.
          </Typography>
        )}
        {notes.map((n) => {
          const mine = !!viewerSub && n.authorSub === viewerSub;
          const canDelete = mine || canModerate;
          return (
            <Box
              key={n.noteId}
              sx={{
                p: 1.25,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              {n.offsetMs != null && onSeek && (
                <Tooltip title={`Jump to ${formatOffset(n.offsetMs)}`}>
                  <IconButton size="small" onClick={() => onSeek(n.offsetMs!)} sx={{ mt: -0.25 }}>
                    <PlayCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25, flexWrap: "wrap" }}>
                  {n.category && <Chip label={n.category} size="small" variant="outlined" />}
                  {n.offsetMs != null && (
                    <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      @ {formatOffset(n.offsetMs)}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {n.authorEmail} · {new Date(n.createdAt).toLocaleString("en-US", { timeZone: "America/Chicago", timeZoneName: "short" })}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {n.text}
                </Typography>
              </Box>
              {canDelete && (
                <IconButton
                  size="small"
                  onClick={() => remove(n.noteId)}
                  disabled={busyDelete === n.noteId}
                  aria-label="Delete note"
                  sx={{ mt: -0.25 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Compose */}
      <Stack spacing={1}>
        <TextField
          size="small"
          multiline
          minRows={2}
          maxRows={6}
          placeholder="Add a coaching note…"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
          inputProps={{ maxLength: MAX_TEXT }}
          fullWidth
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as NoteCategory | "")}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">None</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          {canAnchor && (
            <Tooltip title={`Attach this note to ${formatOffset(currentTimeMs!)}`}>
              <Button
                size="small"
                variant={anchor ? "contained" : "outlined"}
                onClick={() => setAnchor((v) => !v)}
              >
                {anchor ? `Anchored @ ${formatOffset(currentTimeMs!)}` : "Anchor to current time"}
              </Button>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="contained"
            onClick={submit}
            disabled={submitting || text.trim().length === 0}
          >
            {submitting ? "Saving…" : "Add note"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function sortByCreatedAt(list: RecordingNote[]): RecordingNote[] {
  return [...list].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
}

function formatOffset(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
